import dayjs from 'dayjs';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DeleteIcon from '@mui/icons-material/Delete';
import { Button, IconButton, Popover, Stack } from '@mui/material';

import customFilterOperators from '@/shared/components/dataDisplay/DataGridTable/configs/customFilterOperators';
import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';

import TimelineSearchBoxOption from '../../../types/timelineSearchBoxOption.type';

type FilterPopoverProps = {
  index: number;
  anchorEl: HTMLDivElement | null;
  isFilterPanelOpen: boolean;
  activeIndex: number | null;
  selectedOption: TimelineSearchBoxOption;
  setShouldApplyFilters: (value: boolean) => void;
  setIsFilterPanelOpen: (value: boolean) => void;
  setActiveIndex: (value: number | null) => void;
  onApplyFilters: (option: TimelineSearchBoxOption) => void;
};

const FilterPopover: FC<FilterPopoverProps> = ({
  isFilterPanelOpen,
  selectedOption,
  activeIndex,
  anchorEl,
  index,
  setShouldApplyFilters,
  setIsFilterPanelOpen,
  setActiveIndex,
  onApplyFilters,
}) => {
  const { t } = useTranslation(['notes', 'common']);

  const [filters, setFilters] = useState<TimelineSearchBoxOption['filters']>([
    { cop: '', value: '' },
  ]);

  const getOperatorsBySelectedOption = (
    selectedOption: TimelineSearchBoxOption,
  ) => {
    switch (selectedOption.value) {
      case 'user_email_address':
      case 'user_last_name':
      case 'user_first_name':
      case 'module_item_id':
      case 'module_ref':
      case 'note_id':
      case 'note':
        return customFilterOperators.filter((operator) =>
          [
            'ilike',
            'not,ilike',
            'like',
            'not,like',
            'in',
            'not,in',
            'not',
            'is',
            'is,not',
          ].includes(operator.value),
        );
      case 'ts_created':
      case 'ts_modified':
        return customFilterOperators.filter((operator) =>
          ['=', '!=', '>', '<', '>=', '<='].includes(operator.value),
        );
      default:
        return customFilterOperators;
    }
  };

  const isDateField = ['ts_created', 'ts_modified'].includes(
    selectedOption.value,
  );

  const addFilter = () => {
    setFilters([...filters, { cop: '', value: '' }]);
  };

  const removeFilter = (filterIndex: number) => {
    if (filters.length === 1) {
      setFilters([{ cop: '', value: '' }]);
    } else {
      setFilters(filters.filter((_, index) => index !== filterIndex));
    }
  };

  const removeAllFilters = () => {
    setFilters([{ cop: '', value: '' }]);
  };

  const updateFilter = (
    filterIndex: number,
    filter: TimelineSearchBoxOption['filters'][0],
  ) => {
    setFilters(
      filters.map((filterItem, index) =>
        index === filterIndex ? filter : filterItem,
      ),
    );
  };

  return (
    <Popover
      open={isFilterPanelOpen && activeIndex === index}
      anchorEl={anchorEl}
      onClose={() => {
        setIsFilterPanelOpen(false);
        setActiveIndex(null);
      }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          sx: {
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 2,
            width: 450,
          },
        },
      }}
    >
      {filters.map((filter, filterIndex) => (
        <WaveUIForm_V2
          key={filterIndex}
          fields={[
            {
              columnRenderer(field) {
                return (
                  <Stack direction='row' alignItems='center' spacing={1}>
                    <IconButton
                      size='small'
                      onClick={() => removeFilter(filterIndex)}
                    >
                      <DeleteIcon />
                    </IconButton>
                    {field}
                  </Stack>
                );
              },
              columns: 6,
              field_type: FieldType.Select,
              field_props: {
                name: 'cop',
                label: t('notes:filterBy'),
                value: filter.cop,
                placeholder: t('notes:selectFilter'),
                options: getOperatorsBySelectedOption(selectedOption).map(
                  (operator) => ({
                    value: operator.value,
                    label: operator.label as string,
                  }),
                ),
              },
            },
            {
              columns: 6,
              isVisible: !isDateField,
              field_type: FieldType.Text,
              field_props: {
                name: 'value',
                label: 'Value',
                value: filter.value,
                placeholder: t('notes:enterValue'),
              },
            },
            {
              columns: 6,
              isVisible: isDateField,
              field_type: FieldType.DateTime,
              field_props: {
                name: 'value',
                label: 'Value',
                value: dayjs(filter.value || new Date()),
              },
            },
          ]}
          getFieldsValues={(fieldsValues) => {
            const [name, value] = Object.entries(fieldsValues)[0];

            updateFilter(filterIndex, { ...filter, [name]: value });
          }}
        />
      ))}

      <Stack justifyContent='space-between' direction='row'>
        <Button variant='text' size='small' onClick={addFilter}>
          {t('common:addFilter')}
        </Button>

        <Button variant='text' size='small' onClick={removeAllFilters}>
          {t('common:removeAll')}
        </Button>
      </Stack>

      <Stack alignItems='flex-end'>
        <Button
          variant='contained'
          onClick={() => {
            setIsFilterPanelOpen(false);
            setActiveIndex(null);
            setShouldApplyFilters(true);
            onApplyFilters({ ...selectedOption, filters });
          }}
        >
          {t('common:apply')}
        </Button>
      </Stack>
    </Popover>
  );
};

export default FilterPopover;
